import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { AppErrorLogService } from '../services/app-logs/app-error-log.service';
import { CreateAppErrorLogDTO } from '../models/app-logs/create-app-error-log.dto';
import { DataTokenModel } from '../models/data-token.model';
import { EAppPlatform } from '@src/Enum/EAppPlatformEnum';

/**
 * Captura qualquer erro não tratado do app e reporta para o endpoint de log de erros que já
 * existe no backend (hoje só alimentado pelo app mobile). Erros HTTP (HttpErrorResponse) não
 * são reportados aqui: eles já viram um retorno tratado por quem fez a chamada, e reportar de
 * novo aqui só duplicaria ruído (todo 401/404/500 esperado viraria "erro do app").
 *
 * Usa Injector (em vez de injeção direta no construtor) porque um ErrorHandler é criado muito
 * cedo no bootstrap da aplicação; resolver as dependências sob demanda evita que um erro durante
 * o próprio bootstrap quebre a criação do handler.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private readonly injector: Injector) {}

  handleError(error: unknown): void {
    // eslint-disable-next-line no-console
    console.error(error);

    if (error instanceof HttpErrorResponse) {
      return;
    }

    try {
      this.reportarErro(error);
    } catch {
      // Nunca deixar o próprio relatório de erro derrubar a aplicação.
    }
  }

  private reportarErro(error: unknown): void {
    const appErrorLogService = this.injector.get(AppErrorLogService);
    const dataToken = this.obterDadosToken();
    const err = this.normalizarErro(error);

    const dto: CreateAppErrorLogDTO = {
      errorMessage: err.message,
      stackTrace: err.stack,
      errorType: err.name,
      appVersion: 'web',
      platform: EAppPlatform.Web,
      osVersion: navigator.userAgent,
      usuarioId: dataToken?.UsuarioId,
      funcionarioId: dataToken?.FuncionarioId,
      clienteId: dataToken?.ClienteId,
      fazendaId: dataToken?.FazendaId,
      screenName: window.location?.pathname,
      isOnline: navigator.onLine,
      occurredAt: new Date().toISOString(),
    };

    appErrorLogService.registrar(dto).subscribe({
      error: () => {
        // Se nem o log de erro conseguiu ser enviado, não há mais nada a fazer aqui.
      },
    });
  }

  private normalizarErro(error: unknown): { message: string; stack?: string; name: string } {
    if (error instanceof Error) {
      return { message: error.message, stack: error.stack, name: error.name };
    }
    return { message: String(error), name: 'Error' };
  }

  private obterDadosToken(): DataTokenModel | null {
    try {
      const token = localStorage.getItem('token');
      return token ? jwtDecode<DataTokenModel>(token) : null;
    } catch {
      return null;
    }
  }
}
